import React, { createContext, useContext, useRef, useState, useCallback } from 'react';

const MapContext = createContext({
  mapInstance: null,
  setMapInstance: () => {},
  markersCache: {},
  addMarkersToCache: () => {},
  getMarkersFromCache: () => {},
  clearMarkersCache: () => {},
  isMapInitialized: false,
  setMapInitialized: () => {},
  lastViewState: null,
  setLastViewState: () => {},
  tilesCached: false,
  setTilesCached: () => {},
  layersCache: {},
  addLayerToCache: () => {},
  getLayerFromCache: () => {},
  registerPopup: () => {},
  unregisterPopup: () => {},
});

export const MapProvider = ({ children }) => {
  const mapRef = useRef(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [tilesCached, setTilesCached] = useState(false);
  const markersCacheRef = useRef({});
  const layersCacheRef = useRef({});
  const lastViewStateRef = useRef(null);
  const activePopupsRef = useRef(new Set());

  // Set the map instance
  const setMapInstance = (map) => {
    mapRef.current = map;
  };

  // Marker cache functions
  const addMarkersToCache = useCallback((key, markers) => {
    markersCacheRef.current[key] = markers;
  }, []);

  const getMarkersFromCache = useCallback((key) => {
    return markersCacheRef.current[key];
  }, []);

  const clearMarkersCache = useCallback((key) => {
    if (key) {
      delete markersCacheRef.current[key];
    } else {
      markersCacheRef.current = {};
    }
  }, []);

  // Layer cache functions - for storing GeoJSON layers
  const addLayerToCache = useCallback((key, layer) => {
    layersCacheRef.current[key] = layer;
  }, []);

  const getLayerFromCache = useCallback((key) => {
    return layersCacheRef.current[key];
  }, []);

  // Save and retrieve map view state (center, zoom, etc)
  const setLastViewState = useCallback((viewState) => {
    lastViewStateRef.current = viewState;
  }, []);

  // Popup registry to track and clean up popups
  const registerPopup = useCallback((popup) => {
    activePopupsRef.current.add(popup);
  }, []);

  const unregisterPopup = useCallback((popup) => {
    activePopupsRef.current.delete(popup);
  }, []);

  // Clear all active popups
  const clearAllPopups = useCallback(() => {
    activePopupsRef.current.forEach(popup => {
      try {
        if (popup && typeof popup.remove === 'function') {
          popup.remove();
        }
      } catch (e) {
        console.warn('Error removing popup:', e);
      }
    });
    activePopupsRef.current.clear();
  }, []);

  const value = {
    mapInstance: mapRef.current,
    setMapInstance,
    isMapInitialized,
    setMapInitialized: setIsMapInitialized,
    markersCache: markersCacheRef.current,
    addMarkersToCache,
    getMarkersFromCache,
    clearMarkersCache,
    lastViewState: lastViewStateRef.current,
    setLastViewState,
    tilesCached,
    setTilesCached,
    layersCache: layersCacheRef.current,
    addLayerToCache,
    getLayerFromCache,
    registerPopup,
    unregisterPopup,
    clearAllPopups,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

export const useMapContext = () => useContext(MapContext);

export default MapContext;
